import { Dispatch, memo, RefObject, SetStateAction, useCallback } from 'react';

import styles from '../custom-select.module.css';

type CustomSelectInputProps = {
  inputRef: RefObject<HTMLInputElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  selectedOptionsRef: RefObject<HTMLDivElement | null>;
  id: string;
  placeholder?: string;
  lockActiveState: boolean;
  hasSelectedOptions: boolean;
  setIsFocused: Dispatch<SetStateAction<boolean>>;
  setMenuOpen: Dispatch<SetStateAction<boolean>>;
  setSearchValue: Dispatch<SetStateAction<string>>;
};

const CustomSelectInput = memo(
  ({
    id,
    inputRef,
    placeholder,
    setIsFocused,
    setSearchValue,
    menuRef,
    selectedOptionsRef,
    setMenuOpen,
    hasSelectedOptions,
    lockActiveState,
  }: CustomSelectInputProps) => {
    const handleInputOnFocus = useCallback(() => {
      setIsFocused(true);

      // settimeout required to allow classes to catch up before checking
      setTimeout(() => {
        const menuOpen = menuRef.current?.classList.contains(styles['menu-open']);
        if (!menuOpen) {
          setMenuOpen(true);
        }
      }, 0);
    }, [menuRef, setIsFocused, setMenuOpen]);

    return (
      <input
        ref={inputRef}
        id={id}
        name={id}
        type="text"
        aria-label="Search options"
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
        role="search"
        className={`${styles.input} ${hasSelectedOptions ? styles['input-with-selected-options'] : ''} ${lockActiveState ? styles['input-in-select-active-state'] : ''}`}
        placeholder={placeholder}
        onPointerDown={(e) => {
          if (!lockActiveState) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        onClick={() => {
          if (!lockActiveState) {
            inputRef?.current?.focus();
          }
        }}
        onFocus={handleInputOnFocus}
        onBlur={() => {
          setIsFocused(false);
        }}
        onChange={(e) => {
          setSearchValue(e.target.value);
          menuRef?.current?.scrollTo({ top: 0 });
        }}
        onInput={(e) => {
          const target = e.target;
          if (target instanceof HTMLInputElement && target.parentNode instanceof HTMLElement) {
            target.parentNode.setAttribute('data-value', target.value);
          }
          // remove any active remove option btn
          const activeRemoveOption = selectedOptionsRef.current?.querySelector('[data-remove-option-active="true"]');
          if (activeRemoveOption) {
            activeRemoveOption.setAttribute('data-remove-option-active', 'false');
          }
          // if any active menu option, remove active state
          const activeMenuOption = document.querySelector('[data-menu-list-option-active="true"]');
          if (activeMenuOption) {
            activeMenuOption.setAttribute('data-menu-list-option-active', 'false');
          }
          setMenuOpen(true);
        }}
      />
    );
  }
);

CustomSelectInput.displayName = 'CustomSelectInput';

export default CustomSelectInput;
